import { createDraftSafeSelector as createSelector } from '@reduxjs/toolkit';

import { Inject } from '@vk-hr-tek/core/ioc';
import {
  UnitNodeLabeled,
  UnitProcessor,
  UnitType,
} from '@vk-hr-tek/core/units';

import { UserRoleEnum } from '@app/types';

import { DraggableNewsItemType, MappedFeedPostSettings } from '../types';
import {
  SettingsDetailMapper,
  SettingsTemplatesPreviewMapper,
  AttorneysUsersMapper,
  AttorneyDetailMapper,
  AttorneysListMapper,
  VacationsListMapper,
  VacationsMapper,
  VacationDetailMapper,
  VacationStartMapper,
  SettingsCompaniesMapper,
  PartitionMapper,
  SettingsAccountMapper,
  SettingsAbsencesMapper,
  WidgetListMapper,
  WidgetContentNewsListMapper,
  FeedPostsMapper,
  TasksPartitionsMapper,
  SettingsBirthdaysMapper,
  VacationEditDeadlinesMapper,
} from '../mappers';

import {
  accountRoleModelExceptionsAdapter,
  accountDashboardExceptionsAdapter,
  accountBusinessTripsExceptionsAdapter,
  accountsAdapter,
  SettingsWithRootState as RootState,
} from './settings.state';

export const selectCompanies = (state: RootState) => state.settings.companies;

export const selectCompanyById = createSelector(
  [
    (state: RootState) => state.settings.companies,
    (state: RootState, inject: Inject, companyId?: string) => companyId,
  ],
  (companies, companyId) => companies.find(({ id }) => id === companyId),
);

export const selectAccountByAccountId = createSelector(
  [
    (state: RootState) => state.settings.accountsWithSettings.accounts,
    (state: RootState, inject: Inject, accountId?: string) => accountId,
  ],
  (accounts, accountId) =>
    accounts.find((account) => account.account_item.id === accountId),
);

export const selectCompaniesByAccount = createSelector(
  [
    (state: RootState) => state.settings.companiesByAccount,
    (_: RootState, inject: Inject) => inject(SettingsDetailMapper),
  ],
  (companies, format) => format.processCompaniesListByAccount(companies),
);

export const selectAvailableWidgetsStatus = (state: RootState) =>
  state.settings.widgets.status;

export const selectAvailableWidgetsError = (state: RootState) =>
  state.settings.widgets.error;

export const selectAvailableWidgets = createSelector(
  [
    (state: RootState) => state.settings.widgets,
    (_: RootState, inject: Inject) => inject(SettingsDetailMapper),
  ],
  (widgets, format) => format.processWidgets(widgets.items),
);

export const selectIsExceptionCompany = (state: RootState) =>
  state.settings.widgets.isExceptionWidget;

export const selectCanResetCompany = (state: RootState) =>
  state.settings.widgets.canResetLayout;

export const selectCompaniesStatus = (state: RootState) =>
  state.settings.status;

export const selectIsCompanySettingsLoading = createSelector(
  [
    (state: RootState) => state.settings.status,
    (state: RootState) => state.settings.accountsWithSettings.status,
  ],
  (companiesStatus, accountsStatus) =>
    companiesStatus === 'idle' ||
    companiesStatus === 'loading' ||
    accountsStatus === 'idle' ||
    accountsStatus === 'loading',
);

export const selectCompaniesError = (state: RootState) => state.settings.error;

export const selectCompanySignSettingsInitialValues = createSelector(
  [
    (state: RootState) => state.settings.companies,
    (_: RootState, inject: Inject) => inject(SettingsDetailMapper),
    (_: RootState, inject: Inject, id?: string) => id,
  ],
  (companies, service, companyId) => {
    const company = companies.find((companyEl) => companyEl.id === companyId);

    if (!company) {
      return undefined;
    }

    return service.transformCompanySignSettingsInitialValues(
      company.id,
      company.signType,
      company.employeeSignTypeSource,
      company.users,
    );
  },
);

export const selectProfiles = (state: RootState) =>
  state.settings.profiles.items;

export const selectProfilesStatus = (state: RootState) =>
  state.settings.profiles.status;

export const selectProfilesError = (state: RootState) =>
  state.settings.profiles.error;

export const selectCompanyRoles = (state: RootState) =>
  state.settings.companyGroups.items;

export const selectTreeCompanyRoles = createSelector(
  [
    (state: RootState) => state.settings.companyGroups.items,
    (_: RootState, inject: Inject) => inject(SettingsDetailMapper),
  ],
  (groups, mapper) => mapper.transformToTreeCompanyGroup(groups),
);

export const addRolePageStatus = createSelector(
  [
    (state: RootState) => state.settings.status,
    (state: RootState) => state.settings.companyGroups.status,
    (state: RootState) => state.settings.checkCompanyListAccount.status,
    (_: RootState, inject: Inject) => inject(SettingsDetailMapper),
  ],
  (pageStatus, groupsStatus, accountStatus, mapper) =>
    mapper.combineStatuses(pageStatus, groupsStatus, accountStatus),
);

export const selectCompanyGroupsChecked = (state: RootState) => {
  return state.settings.companyGroupsChecked.items;
};

export const selectCompanyGroupsCheckedStatus = (state: RootState) => {
  return state.settings.companyGroupsChecked.status;
};

export const selectCompanyListAccount = (state: RootState) =>
  state.settings.checkCompanyListAccount.items;

export const selectEventsStat = (state: RootState) => state.settings.eventsStat;

export const selectShouldLoadEventStat = createSelector(
  [
    (state: RootState) => state.settings.eventsStat,
    (state: RootState, inject: Inject, companyId?: string) => companyId,
  ],
  (eventsStat, companyId) => {
    if (!companyId) {
      return false;
    }

    const status = eventsStat[companyId]?.status;

    return status !== 'complete' && status !== 'loading';
  },
);

export const selectGroupUsers = (state: RootState) => state.settings.groupUsers;

export const selectAccessLevels = (state: RootState) =>
  state.settings.accessLevels.data;

export const selectError = (state: RootState) => state.settings.error;

export const selectStatus = (state: RootState) => state.settings.status;

export const selectUsersOptionStatus = (state: RootState) =>
  state.settings.users.status;

export const selectTimezoneOptions = createSelector(
  (state: RootState) => state.settings.timezoneOptions.items,
  (items) => items.map(({ value, label }) => ({ value, label })),
);

export const selectTimezoneOptionsWithDisabled = createSelector(
  [
    (state: RootState) => state.settings.timezoneOptions.items,
    (_: RootState, inject: Inject) => inject(SettingsDetailMapper),
    (
      state: RootState,
      inject: Inject,
      params: {
        selectedOptions?: string[];
        companyTimezoneId?: string;
        tooltipText: string;
      },
    ) => params,
  ],
  (timezoneOptions, format, params) =>
    format.processDisabledTimezoneOptions(
      timezoneOptions,
      params.tooltipText,
      params.selectedOptions,
      params.companyTimezoneId,
    ),
);

export const selectCompanyTimezones = createSelector(
  [
    (state: RootState) => state.settings.companyTimezones.items,
    (_: RootState, inject: Inject) => inject(SettingsDetailMapper),
  ],
  (timezones, format) => format.processCompanyTimezones(timezones),
);

export const selectManagerSubstitutes = (state: RootState) =>
  state.settings.managerSubstitutes.items;

export const selectManagerSubstitutesStatus = (state: RootState) =>
  state.settings.managerSubstitutes.status;

export const selectManagerSubstitutesTotal = (state: RootState) =>
  state.settings.managerSubstitutes.total;

export const selectCompanyEventTypes = (state: RootState) =>
  state.settings.companyEventTypes.items;

export const selectCompanyEventTypesStatus = (state: RootState) =>
  state.settings.companyEventTypes.status;

export const selectManagerSubstitutesEventType = (state: RootState) =>
  state.settings.eventTypes;

export const selectCurrentManagerSubstitutesEventTypeStatus = createSelector(
  [
    (state: RootState) => state.settings.eventTypes,
    (state: RootState, inject: Inject, employeeId: string) => employeeId,
  ],
  (managerSubstitutesEventTypes, employeeId) =>
    managerSubstitutesEventTypes[employeeId]?.status,
);

export const selectCurrentGroupedEventTypesByManagerSubstitute = createSelector(
  [
    (state: RootState) => state.settings.eventTypes,
    (state: RootState, inject: Inject) => inject(SettingsDetailMapper),
    (state: RootState, inject: Inject, employeeId: string) => employeeId,
  ],
  (managerSubstitutesEventTypes, service, employeeId) => {
    const currentManagerSubstitutesEventTypes =
      managerSubstitutesEventTypes[employeeId]?.types;

    return currentManagerSubstitutesEventTypes
      ? service.processGroupedEventTypesByManagerSubstitute(
          currentManagerSubstitutesEventTypes,
        )
      : undefined;
  },
);

export const selectEventType = (state: RootState) => state.settings.eventType;
export const selectCurrentEventTypeCollections = (state: RootState) =>
  state.settings.eventType.item?.workflow.collections;
export const selectEventTypeCollections = (state: RootState) =>
  state.settings.eventTypeCollections;

export const selectValidators = createSelector(
  [
    (state: RootState) => state.settings.eventType.item?.workflow.validators,
    (_: RootState, inject: Inject) => inject(SettingsDetailMapper),
  ],
  (validators, format) => format.transformValidators(validators),
);

export const selectWorkFlowTreeStatus = (state: RootState) =>
  state.settings.eventType.status;

export const selectWorkFlowTree = (state: RootState) =>
  state.settings.eventType.item?.treeData;

export const selectGroupOptions = createSelector(
  (state: RootState) => state.settings.eventType.options?.groups,
  (groups) =>
    (groups ?? []).map(({ id: value, name: label }) => ({ value, label })),
);

export const selectWorkflowNodes = (state: RootState) =>
  state.settings.eventType.item?.workflow.nodes || [];

export const selectTemplatePreviewStatus = (state: RootState) =>
  state.settings.templatePreview.status;

export const selectTemplatePreviewError = (state: RootState) =>
  state.settings.templatePreview.error;

export const selectTemplatePreviewInfo = createSelector(
  [
    (state: RootState) => state.settings.templatePreview.data,
    (_: RootState, inject: Inject) => inject(SettingsTemplatesPreviewMapper),
  ],
  (previewData, service) =>
    previewData ? service.processTemplatePreviewInfo(previewData) : null,
);

export const selectTemplatePreviewForm = createSelector(
  [
    (state: RootState) => state.settings.templatePreview.data?.placeholders,
    (_: RootState, inject: Inject) => inject(SettingsTemplatesPreviewMapper),
  ],
  (placeholders, service) =>
    placeholders ? service.processTemplatePreviewForm(placeholders) : null,
);

export const selectTemplateUploadPreviewForm = createSelector(
  [
    (state: RootState) =>
      state.settings.templateUpload.uploadResponsePlaceholders,
    (_: RootState, inject: Inject) => inject(SettingsTemplatesPreviewMapper),
  ],
  (placeholders, service) =>
    placeholders ? service.processTemplatePreviewForm(placeholders) : null,
);

export const selectAttributeOptions = createSelector(
  (state: RootState) =>
    state.settings.eventType.options?.deadline_settings.attributes,
  (attributes) =>
    (attributes ?? []).map(({ id: value, name: label }) => ({ value, label })),
);

export const selectUploadTemplateStatus = (state: RootState) =>
  state.settings.templateUpload.status;

export const selectTemplateInformation = (state: RootState) =>
  state.settings.templateUpload.templateInformation;

export const selectUploadAlertMessage = (state: RootState) =>
  state.settings.templateUpload.uploadAlertMessage;

export const selectUploadedFileIdMessage = (state: RootState) =>
  state.settings.templateUpload.newFileId;

export const selectTemplatesList = (state: RootState) =>
  state.settings.templates.items;

export const selectTemplatesError = (state: RootState) =>
  state.settings.templates.error;

export const selectTemplatesStatus = (state: RootState) =>
  state.settings.templates.status;

export const selectTemplateHistory = (state: RootState) =>
  state.settings.templateHistory.history;

export const selectTemplateHistoryStatus = (state: RootState) =>
  state.settings.templateHistory.status;

export const selectAttorneys = createSelector(
  [
    (state: RootState) => state.settings.attorneys.attorneys,
    (state: RootState) => state.settings.attorneysCompanies.items,
    (_: RootState, inject: Inject) => inject(AttorneysListMapper),
  ],
  (attorneys, companies, mapper) =>
    mapper.processAttorneysList(attorneys, companies),
);

export const selectAttorneysError = (state: RootState) =>
  state.settings.attorneys.error;

export const selectSelectedAttorneys = (state: RootState) =>
  state.settings.attorneys.selected;

export const selectSelectedAttorneysActions = createSelector(
  [
    (state: RootState) => state.settings.attorneys.attorneys,
    (state: RootState) => state.settings.attorneys.selected,
    (state: RootState) => state.settings.attorneysCompanies.items,
    (_: RootState, inject: Inject) => inject(AttorneysListMapper),
  ],
  (attorneys, selected, companies, mapper) =>
    mapper.processSelected(
      mapper
        .processAttorneysList(
          attorneys.filter(({ id }) => selected.includes(id)),
          companies,
        )
        .map(({ actions }) => actions),
    ),
);

export const selectSelectedAttorneysFilters = createSelector(
  [
    (state: RootState) => state.settings.attorneys.filters,
    (_: RootState, inject: Inject) => inject(AttorneysListMapper),
  ],
  (filters, mapper) => mapper.processFilters(filters),
);

export const selectAttorneysStatus = (state: RootState) =>
  state.settings.attorneys.status;

export const selectAttorneysTotal = (state: RootState) =>
  state.settings.attorneys.total;

export const selectAttorneysCompaniesStatus = (state: RootState) =>
  state.settings.attorneysCompanies.status;

export const selectAttorneysCompaniesError = (state: RootState) =>
  state.settings.attorneysCompanies.error;

export const selectAttorneysCompanies = (state: RootState) =>
  state.settings.attorneysCompanies.items;

export const selectAttorneysCompanyById = createSelector(
  [
    (state: RootState) => state.settings.attorneysCompanies.items,
    (state: RootState, inject: Inject, companyId: string) => companyId,
  ],
  (companies, companyId) =>
    companies.find((company) => company.company.id === companyId),
);

export const selectAttorneysUsersStatus = (state: RootState) =>
  state.settings.attorneysUsers.status;

export const selectAttorneysUsersError = (state: RootState) =>
  state.settings.attorneysUsers.error;

export const selectAttorneysUsersFilters = createSelector(
  [
    (state: RootState) => state.settings.attorneysUsers.filters,
    (state: RootState, inject: Inject, companyId: string) => companyId,
    (_: RootState, inject: Inject) => inject(AttorneysUsersMapper),
  ],
  (filters, companyId, mapper) => mapper.processFilters(filters[companyId]),
);

export const selectAttorneysUsersList = createSelector(
  [
    (state: RootState) => state.settings.attorneysUsers.items,
    (_: RootState, inject: Inject) => inject(AttorneysUsersMapper),
  ],
  (users, mapper) => mapper.processUsers(users),
);

export const selectAttorneysUsersTotal = (state: RootState) =>
  state.settings.attorneysUsers.total;

export const selectAttorneysUserById = createSelector(
  [
    (state: RootState) => state.settings.attorneysUsers.items,
    (_: RootState, inject: Inject) => inject(AttorneysUsersMapper),
    (_: RootState, inject: Inject, userId: string) => userId,
  ],
  (users, mapper, userId) => {
    const usersList = mapper.processUsers(users);

    return usersList.find((user) => user.id === userId);
  },
);

export const selectAttorneysUsersByIds = createSelector(
  [
    (state: RootState) => state.settings.attorneysUsers.items,
    (_: RootState, inject: Inject) => inject(AttorneysUsersMapper),
    (_: RootState, inject: Inject, userIds: string[]) => userIds,
  ],
  (users, mapper, userIds) => {
    const usersList = mapper.processUsers(users);

    return usersList.filter((user) => userIds.includes(user.id));
  },
);

export const selectAttorney = createSelector(
  [
    (state: RootState) => state.settings.attorneyDetail.data,
    (state: RootState) => state.settings.attorneysCompanies.items,
    (_: RootState, inject: Inject) => inject(AttorneyDetailMapper),
  ],
  (attorney, companies, mapper) =>
    attorney &&
    mapper.process(attorney, {
      canSign:
        !!companies.length &&
        !!companies.find(
          (company) => company.company.id === attorney.company.id,
        )?.can_sign,
    }),
);

export const selectAttorneyStatus = (state: RootState) =>
  state.settings.attorneyDetail.status;

export const selectAttorneyError = (state: RootState) =>
  state.settings.attorneyDetail.error;

export const selectVacationsListCompanies = createSelector(
  [
    (state: RootState) => state.settings.vacationsList.companies,
    (_: RootState, inject: Inject) => inject(VacationsListMapper),
  ],
  (vacations, mapper) => mapper.processVacationsCompaniesList(vacations),
);

export const selectVacationCompanyById = createSelector(
  [
    (state: RootState) => state.settings.vacationsList.companies,
    (_: RootState, inject: Inject) => inject(VacationsListMapper),
    (_: RootState, inject: Inject, companyId?: string) => companyId,
  ],
  (companies, mapper, companyId) =>
    mapper
      .processVacationsCompaniesList(companies)
      .find((company) => company.id === companyId),
);

export const selectVacationsListError = (state: RootState) =>
  state.settings.vacationsList.error;

export const selectVacationsListStatus = (state: RootState) =>
  state.settings.vacationsList.status;

export const selectStartVacationSchedule = createSelector(
  [
    (state: RootState) => state.settings.vacationDetail.data,
    (_: RootState, inject: Inject) => inject(VacationStartMapper),
    (state: RootState, inject: Inject, companyId?: string) =>
      selectVacationCompanyById(state, inject, companyId),
  ],
  (schedule, mapper, company) =>
    schedule
      ? {
          ...mapper.processVacationsSchedule(schedule),
          companyId: company?.id,
          companyName: company?.name,
        }
      : null,
);

export const selectVacationCreateStep = (state: RootState) =>
  state.settings.vacationCreate.step;

export const selectVacationCreateError = (state: RootState) =>
  state.settings.vacationCreate.error;

export const selectVacationCreateStatus = (state: RootState) =>
  state.settings.vacationCreate.status;

export const selectVacationCreateAdminCompanies = createSelector(
  [
    (state: RootState) => state.settings.vacationCreate.companies,
    (_: RootState, inject: Inject) => inject(VacationsMapper),
  ],
  (vacations, mapper) => mapper.processVacationAdminCompanies(vacations),
);

export const selectGroupOfEmployees = createSelector(
  [
    (state: RootState) =>
      state.settings.vacationCreate.vacationCustomTypes.items,
    (_: RootState, inject: Inject) => inject(VacationsMapper),
  ],
  (customTypes, mapper) => mapper.processVacationCustomTypes(customTypes),
);

export const selectVacationsAvailableTypes = (state: RootState) =>
  state.settings.vacationCreate.vacationCustomTypes.availableTypes;

export const selectVacationCreateFormValues = (state: RootState) =>
  state.settings.vacationCreate.formValues;

export const selectVacationCreateCompanyGroupList = (state: RootState) =>
  state.settings.vacationCreate.companyGroupList;

export const selectVacationEditPositionsStatus = (state: RootState) =>
  state.settings.vacationEdit.positions.status;

export const selectVacationEditHasPositions = (state: RootState) =>
  state.settings.vacationEdit.positions.hasPositions;

export const selectVacationEditEmployeesStatus = (state: RootState) =>
  state.settings.vacationEdit.employees.status;

export const selectVacationEditHasEmployees = (state: RootState) =>
  state.settings.vacationEdit.employees.hasEmployees;

export const selectVacationDetail = createSelector(
  [
    (state: RootState) => state.settings.vacationDetail.data,
    (_: RootState, inject: Inject) => inject(VacationDetailMapper),
  ],
  (schedule, mapper) =>
    schedule ? mapper.processVacationSchedule(schedule) : null,
);

export const selectVacationDetailStatus = (state: RootState) =>
  state.settings.vacationDetail.status;

export const selectVacationDetailError = (state: RootState) =>
  state.settings.vacationDetail.error;

export const selectParticipants = createSelector(
  [
    (state: RootState) => state.settings.eventType.item?.workflow.participants,
    (_: RootState, inject: Inject) => inject(SettingsDetailMapper),
  ],
  (participants, format) =>
    participants?.map((participant) =>
      format.processParticipant(participant),
    ) || [],
);

export const selectParticipantOptions = createSelector(
  (state: RootState) => state.settings.eventType.participantsOptions,
  (options) =>
    (options ?? []).map(({ id: value, name: label }) => ({
      value,
      label,
    })),
);

export const selectIsParticipantsExpanded = (state: RootState) =>
  state.settings.eventType.isParticipantsExpanded;

export const selectSmevErrorsStatus = (state: RootState) =>
  state.settings.smevErrors.status;

export const selectSmevErrorsError = (state: RootState) =>
  state.settings.smevErrors.error;

export const selectIgnoreSmevForEsia = (state: RootState) =>
  state.settings.smevErrors.ignoreSmevForEsia;

export const selectSmevErrorsData = createSelector(
  [
    (state: RootState) => state.settings.smevErrors.data,
    (_: RootState, inject: Inject) => inject(SettingsDetailMapper),
  ],
  (smevErrors, format) =>
    smevErrors?.map((error) => format.processSmevErrors(error)) || [],
);

export const selectCompanyRoleModelStatus = (state: RootState) =>
  state.settings.companyRoleModel.status;

export const selectCompanyRoleModelError = (state: RootState) =>
  state.settings.companyRoleModel.error;

export const selectCompanyRoleModel = createSelector(
  [
    (state: RootState) => state.settings.companyRoleModel.domains,
    (state: RootState) => state.settings.companyRoleModel.groups,
    (_: RootState, inject: Inject) => inject(SettingsDetailMapper),
  ],
  (domains, groups, mapper) => mapper.processCompanyRoleModel(domains, groups),
);

export const selectCompanyGroups = (state: RootState) =>
  state.settings.companyRoleModel.groups;

export const selectCompanyCeoData = createSelector(
  [
    (state: RootState) => state.settings.companyCeo.data,
    (_: RootState, inject: Inject) => inject(SettingsDetailMapper),
  ],
  (companyCeo, format) => format.processCompanyCeo(companyCeo) || [],
);

export const selectCompanyCeoDataStatus = (state: RootState) =>
  state.settings.companyCeo.status;

export const selectCompanyCeoDataError = (state: RootState) =>
  state.settings.companyCeo.error;

export const selectCompanyTimezoneEmployeeItems = createSelector(
  [
    (state: RootState) => state.settings.timezoneEmployees.items,
    (_: RootState, inject: Inject) => inject(SettingsDetailMapper),
  ],
  (items, format) => format.processCompanyTimezoneEmployee(items) || [],
);

export const selectCompanyTimezoneEmployeeError = (state: RootState) =>
  state.settings.timezoneEmployees.error;

export const selectCompanyTimezoneExceptionsItems = createSelector(
  [
    (state: RootState) => state.settings.timezoneExceptions.items,
    (_: RootState, inject: Inject) => inject(SettingsDetailMapper),
  ],
  (items, format) => format.processCompanyTimezoneExceptions(items) || [],
);

export const selectTimezoneExceptionsBySelected = createSelector(
  [
    (state: RootState) => state.settings.timezoneExceptions.items,
    (_: RootState, inject: Inject) => inject(SettingsDetailMapper),
    (_: RootState, inject: Inject, params: { selected: string[] }) => params,
  ],
  (items, format, params) =>
    format.processSelectedTimezoneExceptions(items, params.selected),
);

export const selectCompanyTimezoneExceptionsTotalCount = (state: RootState) =>
  state.settings.timezoneExceptions.totalCount;

export const selectCompanyTimezoneExceptionsError = (state: RootState) =>
  state.settings.timezoneExceptions.error;

export const selectCompanyTimezoneExceptionsFilters = createSelector(
  [
    (state: RootState) => state.settings.timezoneExceptions.filters,
    (_: RootState, inject: Inject) => inject(SettingsDetailMapper),
  ],
  (filters, mapper) =>
    filters && mapper.processTimezoneExceptionsFilters(filters),
);
export const selectSidebarMenuItems = (state: RootState) =>
  state.settings.companySidebar.items;

export const selectSidebarMenuLoadingStatus = (state: RootState) =>
  state.settings.companySidebar.status;

export const selectSidebarRefetchStatus = (state: RootState) =>
  state.settings.companySidebar.needRefetch;

export const selectCompanySidebarError = (state: RootState) =>
  state.settings.companySidebar.error;

export const selectCompanyAccessProfilesSetup = createSelector(
  [
    (state: RootState) => state.settings.companyAccessProfilesSetup.data,
    (_: RootState, inject: Inject) => inject(SettingsCompaniesMapper),
  ],
  (data, service) => data && service.processCompanyProfiles(data),
);

export const selectCompanyAccessProfilesSetupStatus = (state: RootState) =>
  state.settings.companyAccessProfilesSetup.status;

export const selectCompanyAccessProfilesSetupError = (state: RootState) =>
  state.settings.companyAccessProfilesSetup.error;

export const selectCompanyAccessProfilesSetupSeparateUnitsOptions =
  createSelector(
    [
      (state: RootState) =>
        state.settings.companyAccessProfilesSetup.data?.separate_units,
      (_: RootState, inject: Inject) => inject(SettingsCompaniesMapper),
      (_: RootState, inject: Inject, unitType?: UnitType) => unitType,
    ],
    (separateUnits, service, unitType) =>
      service.processSeparateUnits(separateUnits, unitType),
  );

export const selectCompanyAccessProfilesUnitTypes = createSelector(
  [
    (state: RootState) =>
      state.settings.companyAccessProfilesSetup.unitTypes.types,
    (state: RootState) =>
      state.settings.companyAccessProfilesSetup.unitTypes.status,
  ],
  (types, status) => ({
    unitTypesOptions: types.map(({ type, name }) => ({
      value: type,
      label: name,
    })),
    unitTypesStatus: status,
  }),
);

export const selectCompanyAccessProfilesSetupUnitsTreeStatus = (
  state: RootState,
) => state.settings.companyAccessProfilesSetup.unitsTree.status;

export const selectCompanyAccessProfilesSetupUnitsTrees = createSelector(
  [
    (state: RootState) => state.settings.companyAccessProfilesSetup.unitsTrees,
    (_: RootState, inject: Inject) => inject(UnitProcessor),
  ],
  (unitsTrees, unitProcessor) => {
    const mappedUnitsTrees: Record<
      string,
      { root_unit: UnitNodeLabeled } | null
    > = {};

    Object.entries(unitsTrees).forEach(([unitId, unitTree]) => {
      mappedUnitsTrees[unitId] = unitTree.root_unit
        ? { root_unit: unitProcessor.processUnitsTree(unitTree.root_unit) }
        : null;
    });

    return mappedUnitsTrees;
  },
);

export const selectCompanyAccessProfilesSetupUnitsTree = createSelector(
  [
    (state: RootState) =>
      state.settings.companyAccessProfilesSetup.unitsTree.data,
    (_: RootState, inject: Inject) => inject(UnitProcessor),
  ],
  (unitsTree, unitProcessor) => {
    return {
      root_unit: unitsTree?.root_unit
        ? unitProcessor.processUnitsTree(unitsTree.root_unit)
        : null,
    };
  },
);

export const selectAccountStatus = (state: RootState) =>
  state.settings.accountSettings.status;

export const selectAccountError = (state: RootState) =>
  state.settings.accountSettings.error;

const accountsSelectors = accountsAdapter.getSelectors<RootState>(
  (state) => state.settings.accounts,
);

export const selectAccounts = (state: RootState) =>
  accountsSelectors.selectAll(state);

export const selectAccountById = createSelector(
  [
    (state: RootState) => state,
    (_: RootState, inject: Inject, id: string) => id,
  ],
  (state, accountId) => accountsSelectors.selectById(state, accountId),
);

export const selectAccountCompany = (state: RootState) =>
  state.settings.accountCompany.data;

export const selectIsSingleAccountCompany = (state: RootState) =>
  state.settings.accountCompany.isSingleCompany;

export const selectAcountCompaniesNames = (state: RootState) =>
  state.settings.accountSettings.accountCompanies.data.map(({ name }) => name);
export const selectAccountRoleModelStatus = (state: RootState) =>
  state.settings.accountRoleModel.status;

export const selectAccountRoleModelError = (state: RootState) =>
  state.settings.accountRoleModel.error;

export const selectAccountRoleModel = createSelector(
  [
    (state: RootState) => state.settings.accountRoleModel.domains,
    (state: RootState) => state.settings.accountGroups.groups,
    (_: RootState, inject: Inject) => inject(SettingsAccountMapper),
  ],
  (domains, groups, mapper) => mapper.processAccountRoleModel(domains, groups),
);

export const selectAccountRoleModelExceptions = createSelector(
  [
    (state: RootState) => state.settings.accountRoleModelExceptions.currentIds,
    (state: RootState) => state.settings.accountRoleModelExceptions.entities,
  ],
  (currentIds, entities) =>
    currentIds.map((id) => entities[id]).filter(Boolean),
);

const accountRoleModelExceptionsSelectors =
  accountRoleModelExceptionsAdapter.getSelectors<RootState>(
    (state) => state.settings.accountRoleModelExceptions,
  );

export const selectAllAccountRoleModelExceptions = (state: RootState) =>
  accountRoleModelExceptionsSelectors.selectAll(state);

export const selectAccountRoleModelExceptionsTotal = (state: RootState) =>
  state.settings.accountRoleModelExceptions.total;

export const selectAccountRoleModelExceptionsStatus = (state: RootState) =>
  state.settings.accountRoleModelExceptions.status;

export const selectAccountRoleModelExcepitonsError = (state: RootState) =>
  state.settings.accountRoleModelExceptions.error;

export const selectAddAccountRoleModelExceptionsStatus = (state: RootState) =>
  state.settings.addAccountRoleModelExceptions.status;

export const selectAddAccountRoleModelExcepitonsError = (state: RootState) =>
  state.settings.addAccountRoleModelExceptions.error;

export const selectAccountDashboardExceptionsTotal = (state: RootState) =>
  state.settings.accountDashboardExceptions.total;

const accountDashboardExceptionsSelectors =
  accountDashboardExceptionsAdapter.getSelectors<RootState>(
    (state) => state.settings.accountDashboardExceptions,
  );

export const selectAccountDashboardExceptionsStatus = (state: RootState) =>
  state.settings.accountDashboardExceptions.status;

export const selectAccountDashboardExcepitonsError = (state: RootState) =>
  state.settings.accountDashboardExceptions.error;

export const selectAllAccountDashboardExceptions = (state: RootState) =>
  accountDashboardExceptionsSelectors.selectAll(state);

export const selectAccountDashboardExceptions = createSelector(
  [
    (state: RootState) => state.settings.accountDashboardExceptions.currentIds,
    (state: RootState) => state.settings.accountDashboardExceptions.entities,
  ],
  (currentIds, entities) =>
    currentIds.map((id) => entities[id]).filter(Boolean),
);

export const selectAccountBusinessTripsExceptionsTotal = (state: RootState) =>
  state.settings.accountBusinessTripsExceptions.total;

const accountBusinessTripsExceptionsSelectors =
  accountBusinessTripsExceptionsAdapter.getSelectors<RootState>(
    (state) => state.settings.accountBusinessTripsExceptions,
  );

export const selectAccountBusinessTripsExceptionsStatus = (state: RootState) =>
  state.settings.accountBusinessTripsExceptions.status;

export const selectAccountBusinessTripsExceptionsError = (state: RootState) =>
  state.settings.accountBusinessTripsExceptions.error;

export const selectAllAccountBusinessTripsExceptions = (state: RootState) =>
  accountBusinessTripsExceptionsSelectors.selectAll(state);

export const selectAccountBusinessTripsExceptions = createSelector(
  [
    (state: RootState) =>
      state.settings.accountBusinessTripsExceptions.currentIds,
    (state: RootState) =>
      state.settings.accountBusinessTripsExceptions.entities,
  ],
  (currentIds, entities) =>
    currentIds.map((id) => entities[id]).filter(Boolean),
);

export const selectAddAccountBusinessTripsExceptionsStatus = (
  state: RootState,
) => state.settings.addAccountBusinessTripsExceptions.status;

export const selectAddAccountBusinessTripsExceptionsError = (
  state: RootState,
) => state.settings.addAccountBusinessTripsExceptions.error;

export const selectAccountUsersGroups = createSelector(
  [
    (state: RootState) => state.settings.accountUsersGroups.currentIds,
    (state: RootState) => state.settings.accountUsersGroups.entities,
  ],
  (currentIds, entities) =>
    currentIds.map((id) => entities[id]).filter(Boolean),
);

export const selectAccountUsersGroupsTotal = (state: RootState) =>
  state.settings.accountUsersGroups.total;

export const selectAccountUsersGroupsError = (state: RootState) =>
  state.settings.accountUsersGroups.error;

export const selectIsAccountUsersGroupsLoading = (state: RootState) =>
  state.settings.accountUsersGroups.status === 'loading';

export const selectAccountGroupsWithCount = createSelector(
  [
    (state: RootState) => state.settings.accountGroupsWithCount.groups,
    (_: RootState, inject: Inject) => inject(SettingsAccountMapper),
  ],
  (groupsWithCount, format) =>
    format.processAccounGroupsWithCount(groupsWithCount),
);

export const selectWidgetLinks = (state: RootState) =>
  state.settings.widgetLinks.items;

export const selectWidgetLinksError = (state: RootState) =>
  state.settings.widgetLinks.error;

export const selectWidgetLinksStatus = (state: RootState) =>
  state.settings.widgetLinks.status;

export const selectWidgetLinksException = (state: RootState) =>
  state.settings.widgetLinks.isException;

export const selectWidgetLinksHasParent = (state: RootState) =>
  state.settings.widgetLinks.hasParent;

export const selectWidgetBannerException = (state: RootState) =>
  state.settings.banner.isException;

export const selectBannerHasParent = (state: RootState) =>
  !!state.settings.banner.data?.source_widget_id;

export const selectBanner = createSelector(
  [
    (state: RootState) => state.settings.banner.data,
    (_: RootState, inject: Inject) => inject(SettingsDetailMapper),
  ],
  (banner, format) => (banner ? format.processBanner(banner) : null),
);

export const selectBannerError = (state: RootState) =>
  state.settings.banner.error;

export const selectBannerStatus = (state: RootState) =>
  state.settings.banner.status;

export const selectPartitionsStatus = (state: RootState) =>
  state.settings.partitions.status;

export const selectPartitionsIsException = (state: RootState) =>
  state.settings.partitions.isException;

export const selectPartitionsHasParent = (state: RootState) =>
  state.settings.partitions.hasParent;

export const selectPartitionsError = (state: RootState) =>
  state.settings.partitions.error;

export const selectPartitions = createSelector(
  [
    (state: RootState) => state.settings.partitions.items,
    (_: RootState, inject: Inject) => inject(PartitionMapper),
    (_: RootState, inject: Inject, side: UserRoleEnum) => side,
  ],
  (partitions, mapper, side) => mapper.processPartitions(partitions, side),
);

export const selectTasksPartitions = createSelector(
  [
    (state: RootState) => state.settings.partitions.items,
    (state: RootState) => state.settings.partitions.availablePartitions,
    (state: RootState) => state.settings.partitions.requiredPartitions,
    (_: RootState, inject: Inject) => inject(TasksPartitionsMapper),
  ],
  (partitions, availablePartitions, requiredPartitions, mapper) =>
    mapper.proccessTasksPartitions(
      [...partitions, ...availablePartitions],
      requiredPartitions,
    ),
);

export const selectActiveTasksPartitions = createSelector(
  [
    (state: RootState) => state.settings.partitions.items,
    (state: RootState) => state.settings.partitions.availablePartitions,

    (_: RootState, inject: Inject) => inject(TasksPartitionsMapper),
  ],
  (partitions, availablePartitions, mapper) =>
    mapper.proccessActiveTasksPartitions(partitions, availablePartitions),
);

export const selectPartitionsRows = (state: RootState) =>
  state.settings.partitions.rows;

export const selectPartitionsCols = (state: RootState) =>
  state.settings.partitions.cols;

export const selectAvailablePartitions = createSelector(
  [
    (state: RootState) => state.settings.partitions.availablePartitions,
    (_: RootState, inject: Inject) => inject(PartitionMapper),
    (_: RootState, inject: Inject, side: UserRoleEnum) => side,
  ],
  (partitions, mapper, side) =>
    mapper.processAvailablePartitions(partitions, side),
);

export const selectCompanyQuickActionsStatus = (state: RootState) =>
  state.settings.companyQuickActions.status;

export const selectCompanyQuickActionsError = (state: RootState) =>
  state.settings.companyQuickActions.error;

export const selectCompanyActiveQuickActions = (state: RootState) =>
  state.settings.companyQuickActions.activeActions;

export const selectCompanyAvailableQuickActions = (state: RootState) =>
  state.settings.companyQuickActions.availableActions;

export const selectCompanyQuickActionsIsException = (state: RootState) =>
  state.settings.companyQuickActions.isException;

export const selectCompanyQuickActionsHasParent = (state: RootState) =>
  state.settings.companyQuickActions.hasParent;

export const selectAccountGroupsStatus = (state: RootState) =>
  state.settings.accountGroups.status;

export const selectAccountGroups = (state: RootState) =>
  state.settings.accountGroups.groups;

export const selectAccountGroupsNames = (state: RootState) =>
  state.settings.accountGroups.groups.map((group) => group.name);

export const selectTreeAccountGroups = createSelector(
  [
    (state: RootState) => state.settings.accountGroups.groups,
    (_: RootState, inject: Inject) => inject(SettingsAccountMapper),
  ],
  (groups, mapper) => mapper.transformToTreeAccountGroup(groups),
);

export const selectAccountUsersGroupCreationStatus = (state: RootState) =>
  state.settings.accountUsersGroupCreation.status;

export const selectAccountUsersGroupCreationError = (state: RootState) =>
  state.settings.accountUsersGroupCreation.error;

export const selectAccountWithSettings = createSelector(
  [
    (state: RootState) => state.settings.accountsWithSettings.accounts,
    (state: RootState, inject: Inject, id: string) => id,
  ],
  (accounts, id) => accounts.find((account) => account.account_item.id === id),
);

export const selectAbsencesStatus = (state: RootState) =>
  state.settings.absences.status;

export const selectAbsencesError = (state: RootState) =>
  state.settings.absences.error;

export const selectAbsencesData = createSelector(
  [
    (state: RootState) => state.settings.absences.data,
    (_: RootState, inject: Inject) => inject(SettingsAbsencesMapper),
  ],
  (data, mapper) =>
    data
      ? mapper.processAbsencesSettings(data)
      : {
          absence_type_groups: {},
          employee_settings: {},
          manager_settings: {},
        },
);

export const selectAbsencesIsException = (state: RootState) =>
  state.settings.absences.isException;

export const selectAbsencesHasParent = (state: RootState) =>
  !!state.settings.absences.data?.source_widget_id;

export const selectWidgetContentStatus = (state: RootState) =>
  state.settings.widgetContent.status;

export const selectWidgetContentError = (state: RootState) =>
  state.settings.widgetContent.error;

export const selectWidgetContentList = createSelector(
  [
    (state: RootState) => state.settings.widgetContent.data,
    (state: RootState) => state.settings.widgetContent.companies,
    (state: RootState) => state.settings.widgetContent.accounts,
    (_: RootState, inject: Inject) => inject(WidgetListMapper),
  ],
  (widgets, companies, accounts, format) => {
    return format.processWidgets(widgets, companies, accounts);
  },
);

export const selectWidgetContentTotal = (state: RootState) =>
  state.settings.widgetContent.total;

export const selectWidgetContentFilters = createSelector(
  [
    (state: RootState) => state.settings.widgetContent.filters,
    (_: RootState, inject: Inject) => inject(WidgetListMapper),
  ],
  (filters, format) => filters && format.processListFilters(filters),
);

export const selectCompanyTopNewsPreview = createSelector(
  [
    (state: RootState) => state.settings.topNewsDetail.settings,
    (_: RootState, inject: Inject) => inject(SettingsDetailMapper),
  ],
  (topNewsSettings, mapper) => {
    return mapper.processTopNewsPreview(topNewsSettings);
  },
);

export const selectTopNewsDetailSettingsStatus = (state: RootState) =>
  state.settings.topNewsDetail.settings.status;

export const selectTopNewsDetailSettings = createSelector(
  [
    (state: RootState) => state.settings.topNewsDetail.settings.items,
    (_: RootState, inject: Inject) => inject(WidgetListMapper),
  ],
  (news, mapper) => {
    return mapper.processNews(news);
  },
);

export const selectTopNewsDetailSettingsGridLayoutType = (state: RootState) =>
  state.settings.topNewsDetail.settings.type;

export const selectTopNewsCompanyIsException = (state: RootState) =>
  state.settings.topNewsDetail.settings.isException;

export const selectTopNewsHasParent = (state: RootState) =>
  state.settings.topNewsDetail.settings.hasParent;

export const selectTopNewsWidgetSize = createSelector(
  [
    (state: RootState) => state.settings.topNewsDetail.settings.widgetSize,
    (_: RootState, inject: Inject) => inject(WidgetContentNewsListMapper),
  ],
  (widgetSize, mapper) => {
    return mapper.processWidgetSize(widgetSize);
  },
);

export const selectTopNewsDetailListStatus = (state: RootState) =>
  state.settings.topNewsDetail.list.status;

export const selectTopNewsDetailList = createSelector(
  [
    (state: RootState) => state.settings.topNewsDetail.list.items,
    (_: RootState, inject: Inject) => inject(WidgetContentNewsListMapper),
    (
      _: RootState,
      inject: Inject,
      newsItems: { newsIds: string[]; extraNews: DraggableNewsItemType[] },
    ) => newsItems,
  ],
  (news, mapper, { newsIds, extraNews }) => {
    return mapper.processListResponse(news, newsIds, extraNews);
  },
);

export const selectTopNewsDetailListTotalPublished = (state: RootState) =>
  state.settings.topNewsDetail.list.totalPublished;

export const selectTopNewsDetailListFilters = createSelector(
  [
    (state: RootState) => state.settings.topNewsDetail.list.filters,
    (_: RootState, inject: Inject) => inject(WidgetContentNewsListMapper),
  ],
  (filters, mapper) => mapper.processFilters(filters),
);

export const selectTopNewsDetailListImage = createSelector(
  [
    (state: RootState) => state.settings.topNewsDetail.list.images,
    (state: RootState, inject: Inject, newsId: string) => newsId,
  ],
  (images, newsId) => images?.find((news) => news.id === newsId)?.image,
);

export const selectFeedPostsDetailSettings = (state: RootState) =>
  state.settings.feedPostsDetail.settings;

export const selectFeedPostsDetailIsException = (state: RootState) =>
  !!state.settings.feedPostsDetail.settings.isExceptionWidget;

export const selectFeedPostsHasParent = (state: RootState) =>
  state.settings.feedPostsDetail.settings.hasParent;

export const selectFeedPostsDetailPostsListTotal = (state: RootState) =>
  state.settings.feedPostsDetail.postsList.total;

export const selectFeedPostsDetailProcessedFilters = createSelector(
  [
    (state: RootState) => state.settings.feedPostsDetail.filters,
    (_: RootState, inject: Inject, activeTab: MappedFeedPostSettings['type']) =>
      activeTab,
    (_: RootState, inject: Inject) => inject(FeedPostsMapper),
  ],
  (filters, activeTab, mapper) => mapper.processFilters(filters, activeTab),
);

export const selectFeedPostsDetailTabOptions = createSelector(
  [
    (state: RootState) => state.settings.feedPostsDetail.filters,
    (_: RootState, inject: Inject) => inject(FeedPostsMapper),
  ],
  (filters, mapper) => mapper.processTabOptions(filters),
);

export const selectFeedPostsDetailNewsListTotal = (state: RootState) =>
  state.settings.feedPostsDetail.newsList.total;

export const selectFeedPostsDetailPostsListStatus = (state: RootState) =>
  state.settings.feedPostsDetail.postsList.status;

export const selectFeedPostsDetailNewsListStatus = (state: RootState) =>
  state.settings.feedPostsDetail.newsList.status;

export const selectFeedPostsDetailMappedPostsList = createSelector(
  [
    (state: RootState) => state.settings.feedPostsDetail.postsList.items,
    (state: RootState) => state.settings.feedPostsDetail.settings.pinnedPostId,
    (_: RootState, inject: Inject) => inject(FeedPostsMapper),
  ],
  (posts, pinnedPostId, mapper) =>
    mapper.processMapPostsList(posts, pinnedPostId),
);

export const selectFeedPostsDetailMappedNewsList = createSelector(
  [
    (state: RootState) => state.settings.feedPostsDetail.newsList.items,
    (state: RootState) => state.settings.feedPostsDetail.settings.pinnedNewsId,
    (_: RootState, inject: Inject) => inject(FeedPostsMapper),
  ],
  (newsList, pinnedNewsId, mapper) =>
    mapper.processMapNewsList(newsList, pinnedNewsId),
);

export const selectWidgetContent = createSelector(
  [
    (state: RootState) => state.settings.feedPostsDetail.widget,
    (state: RootState) => state.settings.widgetContent.companies,
    (state: RootState) => state.settings.widgetContent.accounts,
    (_: RootState, inject: Inject) => inject(WidgetListMapper),
    (state: RootState, inject: Inject, companyId: string | undefined) =>
      companyId,
  ],
  (widget, companies, accounts, format, companyId) => {
    if (!widget) {
      return undefined;
    }
    const formatted = format.processWidgetWithCompanyId(
      widget,
      companies,
      accounts,
      companyId,
    );
    return formatted;
  },
);

export const selectBirthdaysSettingsStatus = (state: RootState) =>
  state.settings.birthdays.status;

export const selectBirthdaysSettingsError = (state: RootState) =>
  state.settings.birthdays.error;

export const selectBirthdaysSettingsData = createSelector(
  [
    (state: RootState) => state.settings.birthdays.data,
    (_: RootState, inject: Inject) => inject(SettingsBirthdaysMapper),
  ],
  (data, mapper) =>
    data
      ? mapper.processBirthdaysSettings(data)
      : {
          employee_settings: {},
          manager_settings: {},
        },
);

export const selectBirthdaysSettingsIsException = (state: RootState) =>
  state.settings.birthdays.isException;

export const selectBirthdaysHasParent = (state: RootState) =>
  !!state.settings.birthdays.data?.source_widget_id;

export const selectLegalUnitsTree = createSelector(
  (state: RootState) => state.settings.vacationCreate.unitsTree,
  (_: RootState, inject: Inject) => inject(VacationsMapper),
  (unitsTree, mapper) => {
    return {
      root_unit: unitsTree?.legal.root_unit
        ? mapper.processUnitsTree(unitsTree.legal.root_unit)
        : null,
    };
  },
);

export const selectOperationalUnitsTree = createSelector(
  (state: RootState) => state.settings.vacationCreate.unitsTree,
  (_: RootState, inject: Inject) => inject(VacationsMapper),
  (unitsTree, mapper) => {
    return {
      root_unit: unitsTree?.operational.root_unit
        ? mapper.processUnitsTree(unitsTree.operational.root_unit)
        : null,
    };
  },
);

export const selectCatalogCollections = createSelector(
  [
    (state: RootState) => state.settings.collections.items,
    (_: RootState, inject: Inject) => inject(SettingsCompaniesMapper),
  ],
  (data, mapper) => mapper.processCompanyCatalogCollections(data),
);

export const selectCatalogCollectionsStatus = (state: RootState) =>
  state.settings.collections.status;

export const selectCatalogCollectionsError = (state: RootState) =>
  state.settings.collections.error;

export const selectEventTypes = createSelector(
  [
    (state: RootState) => state.settings.eventTypesList.items,
    (_: RootState, inject: Inject) => inject(SettingsCompaniesMapper),
  ],
  (eventTypes, mapper) => mapper.processEventTypesList(eventTypes),
);

export const selectEventTypesStatus = (state: RootState) =>
  state.settings.eventTypesList.status;

export const selectEventTypesError = (state: RootState) =>
  state.settings.eventTypesList.error;

export const selectCollectionName = (state: RootState) =>
  state.settings.eventTypesList.collectionName;

export const selectVacationEditDeadlinesSchedule = createSelector(
  [
    (state: RootState) => state.settings.vacationDetail.data,
    (_: RootState, inject: Inject) => inject(VacationEditDeadlinesMapper),
  ],
  (schedule, mapper) =>
    schedule ? mapper.processVacationSchedule(schedule) : null,
);
